import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Minus, 
  Plus, 
  ShoppingCart, 
  Heart, 
  Share2, 
  Star,
  Truck,
  Shield,
  RefreshCw,
  Check,
  ArrowLeft
} from 'lucide-react';
import { neo4jService } from '../../services/neo4jService';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../contexts/ToastContext';
import { DEFAULT_PRODUCT_CONDITION } from '../../config/productCondition';
import { DEFAULT_PRODUCT_POLICIES } from '../../config/productPolicies';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { currentUser } = useAuth();
  
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedTab, setSelectedTab] = useState('description');
  const [product, setProduct] = useState({
    id: '',
    name: '',
    price: 0,
    condition: DEFAULT_PRODUCT_CONDITION,
    description: '',
    ...DEFAULT_PRODUCT_POLICIES,
    imageUrl: '',
    rating: 0,
    reviewsCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRating, setUserRating] = useState(0);
  const [isRatingHovered, setIsRatingHovered] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        const productData = await neo4jService.getProductById(id);
        
        if (productData) {
          setProduct({
            ...DEFAULT_PRODUCT_POLICIES,
            ...productData,
            rating: productData.rating || 0,
            reviewsCount: productData.reviewsCount || 0
          });
          setError(null);
        } else {
          setError('Product not found');
          navigate('/404', { replace: true });
        }
      } catch (err) {
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      loadProduct();
    }
  }, [id, navigate]);

  useEffect(() => {
    const loadUserRating = async () => {
      if (currentUser && product) {
        const rating = await neo4jService.getUserRating(product.id, currentUser.email);
        if (rating) {
          setUserRating(rating);
        }
      }
    };
    loadUserRating();
  }, [product, currentUser]);

  useEffect(() => {
    const hasSpecifications = product.specifications &&
      typeof product.specifications === 'object' &&
      Object.values(product.specifications).some(value => String(value || '').trim() !== '');
    const hasReviews = Number(product.reviewsCount) > 0;

    if ((selectedTab === 'specifications' && !hasSpecifications) || (selectedTab === 'reviews' && !hasReviews)) {
      setSelectedTab('description');
    }
  }, [product.specifications, product.reviewsCount, selectedTab]);

  if (loading || !product) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const handleQuantityChange = (value) => {
    setQuantity(prev => Math.max(1, Math.min(product.qty_instock, prev + value)));
  };

  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.main_image,
      qty_instock: product.qty_instock
    }, quantity);
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleRatingClick = async (newRating) => {
    if (!currentUser) {
      toast.error('Please log in to rate this product');
      return;
    }

    try {
      const result = await neo4jService.addRating(product.id, currentUser.email, newRating);
      setUserRating(parseFloat(newRating));
      setProduct(prev => ({
        ...prev,
        rating: parseFloat(result.rating),
        reviewsCount: parseInt(result.reviewsCount)
      }));
      toast.success('Rating updated successfully');
    } catch (error) {
      console.error('Error updating rating:', error);
      toast.error('Failed to update rating');
    }
  };

  const handleRatingHover = (rating) => {
    if (!currentUser) {
      return;
    }

    setIsRatingHovered(true);
    setHoveredRating(rating);
  };

  const handleRatingLeave = () => {
    if (!currentUser) {
      return;
    }

    setIsRatingHovered(false);
    setHoveredRating(0);
  };

  const parsedPrice = Number(product.price);
  const parsedOriginalPrice = Number(product.originalPrice);
  const hasValidPrice = Number.isFinite(parsedPrice);
  const hasValidOriginalPrice = Number.isFinite(parsedOriginalPrice) && parsedOriginalPrice > 0;

  const renderStars = () => {
    const displayRating = isRatingHovered ? 
      parseFloat(hoveredRating) : 
      (parseFloat(userRating) || parseFloat(product?.rating) || 0);
    
    return (
      <div 
        className="stars-container" 
        onMouseLeave={handleRatingLeave}
      >
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`star ${star <= displayRating ? 'filled' : ''} ${currentUser ? 'interactive' : ''}`}
            onClick={() => handleRatingClick(star)}
            onMouseEnter={currentUser ? () => handleRatingHover(star) : undefined}
            fill={star <= displayRating ? "#ffd700" : "#e4e5e9"}
          />
        ))}
        <span className="rating-count">
          ({product?.reviewsCount || 0} {product?.reviewsCount === 1 ? 'review' : 'reviews'})
        </span>
      </div>
    );
  };

  const discount = hasValidPrice && hasValidOriginalPrice && parsedOriginalPrice > parsedPrice
    ? Math.round((1 - parsedPrice / parsedOriginalPrice) * 100)
    : null;
  const productFeatures = Array.isArray(product.features)
    ? product.features.filter(feature => String(feature || '').trim() !== '')
    : [];
  const productSpecifications = product.specifications && typeof product.specifications === 'object'
    ? Object.entries(product.specifications).filter(([, value]) => String(value || '').trim() !== '')
    : [];
  const hasReviews = Number(product.reviewsCount) > 0;
  const availableTabs = [
    { id: 'description', label: 'Description' },
    ...(productSpecifications.length > 0 ? [{ id: 'specifications', label: 'Specifications' }] : []),
    ...(hasReviews ? [{ id: 'reviews', label: `Reviews (${product.reviewsCount})` }] : [])
  ];
  const policyCards = [
    {
      id: 'shipping',
      enabled: product.shippingTitle || product.shippingDescription || product.shippingCost,
      icon: <Truck size={24} />,
      title: product.shippingTitle,
      description: product.shippingDescription,
      cost: product.shippingCost,
      color: '#4CAF50'
    },
    {
      id: 'delivery',
      enabled: product.deliveryTitle || product.deliveryDescription || product.deliveryCost,
      icon: <Truck size={24} />,
      title: product.deliveryTitle,
      description: product.deliveryDescription,
      cost: product.deliveryCost
    },
    {
      id: 'warranty',
      enabled: product.warrantyTitle || product.warrantyDescription,
      icon: <Shield size={24} />,
      title: product.warrantyTitle,
      description: product.warrantyDescription
    },
    {
      id: 'returns',
      enabled: product.returnTitle || product.returnDescription,
      icon: <RefreshCw size={24} />,
      title: product.returnTitle,
      description: product.returnDescription
    }
  ].filter(card => card.enabled);

  return (
    <div className="product-detail">
     <button className="back-button" onClick={handleGoBack}>
        <div className="back-button-content">
        <ArrowLeft size={20} />
        <span>Back to Products</span>
        </div>
      </button>
      <div className="product-detail-container">
        {/* Product Images Section */}
        <div className="product-images">
          <div className="main-image">
            <img 
              src={product.main_image} 
              alt={product.name} 
            />
          </div>
          <div className="image-thumbnails">
            {product.images && product.images.length > 0 ? (
              product.images.map((image, index) => (
                <div 
                  key={index}
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                >
                  <img src={image} alt={`${product.name} ${index + 1}`} />
                </div>
              ))
            ) : (
              <div className="thumbnail active">
                <img src={product.main_image} alt={product.name} />
              </div>
            )}
          </div>
        </div>

        {/* Product Info Section */}
        <div className="product-info">
          <div className="product-header">
            <h1 className="product-title">{product.brand.toLocaleUpperCase()}</h1>
            <div className="product-title-description">{product.description}</div>
            <div className="product-meta">
              <div className="product-rating">
                {renderStars()}
              </div>
              <div className="product-sku">
                SKU: <span>{product.sku}</span>
              </div>
              <div className="product-condition-detail">
                Condition: <span>{product.condition || DEFAULT_PRODUCT_CONDITION}</span>
              </div>
            </div>
          </div>

          <div className="product-pricing">
            <div className="price-container">
              <span className="current-price">
                {hasValidPrice ? `$${parsedPrice.toFixed(2)}` : 'Price unavailable'}
              </span>
              {hasValidOriginalPrice && (
                <>
                  <span className="original-price">${parsedOriginalPrice.toFixed(2)}</span>
                  {discount !== null && (
                    <span className="discount-badge">{discount}% OFF</span>
                  )}
                </>
              )}
            </div>
            <div className={`stock-status ${product.inStock ? 'in-stock' : 'out-of-stock'}`}>
              <Check size={16} />
              {product.availability}
            </div>
          </div>

          {product.inStock && (
            <div className="action-section">
              <div className="basic-quantity">
                <button 
                  onClick={() => handleQuantityChange(-1)} 
                  disabled={quantity <= 1}
                >
                  <Minus size={16} />
                </button>
                <span>{quantity}</span>
                <button 
                  onClick={() => handleQuantityChange(1)} 
                  disabled={quantity >= product.qty_instock}
                >
                  <Plus size={16} />
                </button>
              </div>
              
              <button 
                className="basic-cart-button" 
                onClick={handleAddToCart}
              >
                <ShoppingCart size={20} />
                Add to Cart
              </button>

              <button 
                className={`action-button ${isFavorite ? 'active' : ''}`}
                onClick={toggleFavorite}
              >
                <Heart size={20} />
              </button>

              <button 
                className="action-button"
                onClick={handleShare}
              >
                <Share2 size={20} />
              </button>
            </div>
          )}

          {policyCards.length > 0 && (
            <div className="product-benefits">
              {policyCards.map(policy => (
                <div
                  key={policy.id}
                  className={`benefit ${policy.color ? 'configurable-benefit' : ''}`}
                  style={policy.color ? { '--benefit-color': policy.color } : undefined}
                >
                  {policy.icon}
                  <div className="benefit-content">
                    {policy.title && <h4>{policy.title}</h4>}
                    {policy.description && <p>{policy.description}</p>}
                    {policy.cost && <span className="benefit-cost">Cost: {policy.cost}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Product Details Tabs */}
          <div className="product-tabs">
            <div className="tabs-header">
              {availableTabs.map(tab => (
                <button
                  key={tab.id}
                  className={`tab ${selectedTab === tab.id ? 'active' : ''}`}
                  onClick={() => setSelectedTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="tab-content">
              {selectedTab === 'description' && (
                <div className="description-content">
                  <p>{product.description}</p>
                  {productFeatures.length > 0 && (
                    <>
                      <h3>Key Features</h3>
                      <ul className="features-list">
                        {productFeatures.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}

              {selectedTab === 'specifications' && (
                <div className="specifications-content">
                  <table className="specs-table">
                    <tbody>
                      {productSpecifications.map(([key, value]) => (
                        <tr key={key}>
                          <td className="spec-name">{key}</td>
                          <td className="spec-value">{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedTab === 'reviews' && (
                <div className="reviews-content">
                  <div className="reviews-summary">
                    <h3>Customer Reviews</h3>
                    <div className="rating-summary">
                      <div className="average-rating">
                        <span className="rating-number">{product.rating}</span>
                        <div className="stars">
                          {renderStars()}
                        </div>
                        <span className="total-reviews">
                          Based on {product.reviewsCount} reviews
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
